/** Semantic identity / address presentation. Display-only: stored values are never changed.
 *  Person + organisation names: BOLD UPPERCASE. CITY + PIN and STATE + COUNTRY: BOLD UPPERCASE UNDERLINED.
 *  Never use these for email, URL, IDs, serial / SKU / model, message bodies or logos. */
export const up = (v) => (v == null ? "" : String(v).trim().toLocaleUpperCase("en-IN"));
export const sameText = (a, b) => up(a) === up(b);
export const ID_INPUT = "font-bold uppercase";
const B = "font-bold uppercase";
const U = "font-bold uppercase underline decoration-1 underline-offset-2";

export function PersonName({ salutation, name, className = "", testid }) {
  const s = salutation && salutation !== "Other" ? `${salutation} ` : "";
  return <span className={`${B} ${className}`} data-testid={testid}>{up(`${s}${name || ""}`)}</span>;
}

export function OrgName({ name, className = "", testid }) {
  return name ? <span className={`${B} ${className}`} data-testid={testid}>{up(name)}</span> : null;
}

export const cityPinText = (city, pin) => [up(city), up(pin)].filter(Boolean).join(" – ");
export const stateCountryText = (state, country) => [up(state), up(country)].filter(Boolean).join(", ");

export function CityPin({ city, pin, className = "", testid }) {
  const t = cityPinText(city, pin);
  return t ? <span className={`${U} ${className}`} data-testid={testid}>{t}</span> : null;
}

export function StateCountry({ state, country, className = "", testid }) {
  const t = stateCountryText(state, country);
  return t ? <span className={`${U} ${className}`} data-testid={testid}>{t}</span> : null;
}

/** Street lines keep their stored newlines; city/PIN and state/country each get their own line. */
export function AddressBlock({ street, city, pin, state, country, className = "", testid }) {
  return (
    <div className={`break-words leading-5 ${className}`} data-testid={testid}>
      {street && <div className="whitespace-pre-line">{street}</div>}
      {(city || pin) && <div><CityPin city={city} pin={pin} testid={testid && `${testid}-citypin`} /></div>}
      {(state || country) && <div><StateCountry state={state} country={country} testid={testid && `${testid}-statecountry`} /></div>}
    </div>
  );
}

/** Canonical secondary delivery contact (same precedence as the backend): structured secondary_contact, else legacy alt_phone as a number only. */
export function secondaryOf(a) {
  const sc = a?.secondary_contact || {};
  if (sc.phone || sc.number || sc.name) return { name: sc.name || null, relationship: sc.relationship || null, phone: sc.phone || sc.number || null, source: sc.source || "STAFF_ENTERED" };
  return a?.alt_phone ? { name: null, relationship: null, phone: a.alt_phone, source: "LEGACY_ALT_PHONE" } : null;
}

/** "Ph: primary | alternate" — same rule as backend phone_line: max two numbers, duplicates dropped, repeated country code omitted. */
export function phoneLine(a) {
  const cc = (n) => (/^\+(\d{1,3}) /.exec(n) || [])[1];
  const out = []; const seen = new Set();
  for (const raw of [a?.phone, secondaryOf(a)?.phone]) {
    const n = String(raw || "").trim(); const k = n.replace(/\D/g, "").slice(-10);
    if (!n || seen.has(k)) continue;
    seen.add(k); out.push(out.length && cc(n) && cc(n) === cc(out[0]) ? n.split(" ").slice(1).join(" ") : n);
    if (out.length === 2) break;
  }
  return out.length ? `Ph: ${out.join(" | ")}` : "";
}

export function SecondaryContactLine({ a, className = "", testid, hidePhone = false }) {
  const s = secondaryOf(a);
  if (!s || (hidePhone && !s.name)) return null;
  return (
    <div className={className} data-testid={testid}>
      <span className="text-slate-500">{s.source === "LEGACY_ALT_PHONE" ? "Alt. mobile (owner not recorded): " : "Secondary contact: "}</span>
      {s.name && <span className={B}>{up(s.name)}</span>}{s.relationship && <span className="text-slate-500"> ({s.relationship})</span>}{!hidePhone && s.name && s.phone ? " · " : ""}{!hidePhone && s.phone && <span>{s.phone}</span>}
      {s.source === "EMPLOYEE_PROPOSED_REVIEW_APPROVED" && <span className="ml-1 rounded bg-emerald-50 px-1 text-[9px] font-semibold text-emerald-800">EMPLOYEE-CONFIRMED</span>}
    </div>
  );
}
